import type { ReactElement, ReactNode } from 'react'
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase, formatAuthError } from '@/lib/supabase'
import type { UserType, Profile, MemberRole, School, Recruiter } from '@/types/database.types'

// Organization context for the current user
export interface OrganizationInfo {
  type: 'school' | 'recruiter'
  id: string
  role: MemberRole
  name: string
  school?: School
  recruiter?: Recruiter
}

interface AuthState {
  user: User | null
  profile: Profile | null
  session: Session | null
  // Current organization (for school/recruiter users)
  organization: OrganizationInfo | null
  // All organizations the user belongs to (for multi-org support)
  organizations: OrganizationInfo[]
  loading: boolean
  initialized: boolean
}

interface AuthContextType extends AuthState {
  signUp: (
    email: string,
    password: string,
    userType: UserType,
    fullName: string
  ) => Promise<{ error: string | null }>
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<{ error: string | null }>
  resetPassword: (email: string) => Promise<{ error: string | null }>
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>
  refreshProfile: () => Promise<void>
  refreshOrganization: () => Promise<void>
  switchOrganization: (orgId: string, orgType: 'school' | 'recruiter') => void
  // Convenience getters
  schoolId: string | undefined
  recruiterId: string | undefined
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

// Fetch profile from database
async function fetchProfile(userId: string): Promise<Profile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (error) {
      console.error('Error fetching profile:', error)
      return null
    }

    return data ? (data as Profile) : null
  } catch (err) {
    console.error('Profile fetch failed:', err)
    return null
  }
}

// Fetch organization memberships
async function fetchOrganizations(userId: string, userType?: string): Promise<OrganizationInfo[]> {
  try {
    const organizations: OrganizationInfo[] = []

    // Job seekers don't have organization memberships
    if (userType === 'job_seeker') {
      return organizations
    }

    // Only fetch school memberships for school users (or if user_type unknown)
    if (!userType || userType === 'school') {
      const { data: schoolMemberships, error: schoolError } = await supabase
        .from('school_members')
        .select(`
          school_id,
          role,
          school:schools(*)
        `)
        .eq('user_id', userId)

      if (schoolError) {
        console.error('Error fetching school memberships:', schoolError)
      } else if (schoolMemberships) {
        interface SchoolMembershipRow {
          school_id: string
          role: MemberRole
          school: School | null
        }
        (schoolMemberships as SchoolMembershipRow[]).forEach((m) => {
          if (m.school) {
            organizations.push({
              type: 'school',
              id: m.school_id,
              role: m.role,
              name: m.school.name,
              school: m.school,
            })
          }
        })
      }
    }

    // Only fetch recruiter memberships for recruiter users (or if user_type unknown)
    if (!userType || userType === 'recruiter') {
      const { data: recruiterMemberships, error: recruiterError } = await supabase
        .from('recruiter_members')
        .select(`
          recruiter_id,
          role,
          recruiter:recruiters(*)
        `)
        .eq('user_id', userId)

      if (recruiterError) {
        console.error('Error fetching recruiter memberships:', recruiterError)
      } else if (recruiterMemberships) {
        interface RecruiterMembershipRow {
          recruiter_id: string
          role: MemberRole
          recruiter: Recruiter | null
        }
        (recruiterMemberships as RecruiterMembershipRow[]).forEach((m) => {
          if (m.recruiter) {
            organizations.push({
              type: 'recruiter',
              id: m.recruiter_id,
              role: m.role,
              name: m.recruiter.company_name,
              recruiter: m.recruiter,
            })
          }
        })
      }
    }

    return organizations
  } catch (err) {
    console.error('Organization fetch failed:', err)
    return []
  }
}

// Resolve full auth state for a session (profile + orgs)
async function resolveFullAuthState(session: Session): Promise<Omit<AuthState, 'loading' | 'initialized'>> {
  const profile = await fetchProfile(session.user.id)
  const organizations = await fetchOrganizations(session.user.id, profile?.user_type)

  // Update last_active_at for job seekers (fire-and-forget)
  if (profile?.user_type === 'job_seeker') {
    void supabase
      .from('job_seekers')
      .update({ last_active_at: new Date().toISOString() })
      .eq('user_id', session.user.id)
  }

  const persistedOrgId = localStorage.getItem('selectedOrgId')
  const persistedOrgType = localStorage.getItem('selectedOrgType')

  let organization = organizations[0] || null
  if (persistedOrgId && persistedOrgType) {
    const selected = organizations.find(
      (o) => o.id === persistedOrgId && o.type === persistedOrgType
    )
    if (selected) { organization = selected }
  }

  return {
    user: session.user,
    profile,
    session,
    organization,
    organizations,
  }
}

const UNAUTHENTICATED_STATE: AuthState = {
  user: null,
  profile: null,
  session: null,
  organization: null,
  organizations: [],
  loading: false,
  initialized: true,
}

function clearStoredAuth(): void {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
      localStorage.removeItem(key)
    }
  }
  localStorage.removeItem('selectedOrgId')
  localStorage.removeItem('selectedOrgType')
}

export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    session: null,
    organization: null,
    organizations: [],
    loading: true,
    initialized: false,
  })

  // Use a ref to track if we're currently resolving auth state,
  // and to cancel stale resolutions.
  const resolveCounterRef = useRef(0)

  // Core function: set authenticated state from a session.
  // Uses a counter to discard results from stale calls.
  const setAuthenticatedFromSession = useCallback(async (session: Session) => {
    const myCounter = ++resolveCounterRef.current

    setState((prev) => ({
      ...prev,
      user: session.user,
      session,
      loading: true,
      initialized: true,
    }))

    try {
      const resolved = await resolveFullAuthState(session)

      // Only apply if this is still the latest call
      if (resolveCounterRef.current !== myCounter) { return }

      setState({
        ...resolved,
        loading: false,
        initialized: true,
      })
    } catch (err) {
      console.error('Error resolving auth state:', err)
      if (resolveCounterRef.current !== myCounter) { return }

      setState({
        user: session.user,
        profile: null,
        session,
        organization: null,
        organizations: [],
        loading: false,
        initialized: true,
      })
    }
  }, [])

  const setUnauthenticated = useCallback(() => {
    resolveCounterRef.current++
    clearStoredAuth()
    setState(UNAUTHENTICATED_STATE)
  }, [])

  // Initialize auth + listen for changes
  useEffect(() => {
    let cancelled = false

    const init = async (): Promise<void> => {
      try {
        // Race getSession against a 5s timeout.
        // getSession can hang if the Navigator Lock is stuck or
        // the refresh token is expired/revoked.
        let sessionResult: Awaited<ReturnType<typeof supabase.auth.getSession>> | null
        try {
          sessionResult = await Promise.race([
            supabase.auth.getSession(),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
          ])
        } catch (err) {
          console.warn('Auth init: getSession failed', err)
          if (!cancelled) { setUnauthenticated() }
          return
        }

        if (cancelled) { return }

        if (!sessionResult || sessionResult.error || !sessionResult.data.session) {
          setUnauthenticated()
          return
        }

        const session = sessionResult.data.session

        // Check if token is expired
        const expiresAt = session.expires_at
        if (expiresAt && expiresAt * 1000 < Date.now()) {
          let refreshResult: Awaited<ReturnType<typeof supabase.auth.refreshSession>> | null
          try {
            refreshResult = await Promise.race([
              supabase.auth.refreshSession(),
              new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
            ])
          } catch {
            if (!cancelled) { setUnauthenticated() }
            return
          }

          if (cancelled) { return }

          if (!refreshResult || refreshResult.error || !refreshResult.data.session) {
            setUnauthenticated()
            return
          }

          await setAuthenticatedFromSession(refreshResult.data.session)
          return
        }

        if (!cancelled) {
          await setAuthenticatedFromSession(session)
        }
      } catch (err) {
        console.error('Auth init error:', err)
        if (!cancelled) { setUnauthenticated() }
      }
    }

    init()

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) { return }

      // INITIAL_SESSION is handled by init() above
      if (event === 'INITIAL_SESSION') {
        return
      }

      // Sign out — clear everything immediately
      if (event === 'SIGNED_OUT' || !session) {
        localStorage.removeItem('selectedOrgId')
        localStorage.removeItem('selectedOrgType')
        resolveCounterRef.current++
        setState(UNAUTHENTICATED_STATE)
        return
      }

      // Non-disruptive events: just update session, don't refetch
      if (event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED') {
        setState((prev) => ({
          ...prev,
          user: session.user,
          session,
        }))
        return
      }

      // SIGNED_IN: fetch profile + orgs
      // Note: this callback is NOT async — we call the async function
      // but don't await it (Supabase doesn't await the callback anyway).
      // The counter-based approach in setAuthenticatedFromSession handles
      // concurrent calls safely.
      void setAuthenticatedFromSession(session)
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [setAuthenticatedFromSession, setUnauthenticated])

  const refreshProfile = useCallback(async (): Promise<void> => {
    if (!state.user) { return }

    const profile = await fetchProfile(state.user.id)
    setState((prev) => ({ ...prev, profile }))
  }, [state.user])

  const refreshOrganization = useCallback(async (): Promise<void> => {
    if (!state.user) { return }

    const organizations = await fetchOrganizations(state.user.id, state.profile?.user_type)

    const persistedOrgId = localStorage.getItem('selectedOrgId')
    const persistedOrgType = localStorage.getItem('selectedOrgType')

    let currentOrg = organizations[0] || null
    if (persistedOrgId && persistedOrgType) {
      const selected = organizations.find(
        (o) => o.id === persistedOrgId && o.type === persistedOrgType
      )
      if (selected) { currentOrg = selected }
    }

    setState((prev) => ({ ...prev, organizations, organization: currentOrg }))
  }, [state.user, state.profile?.user_type])

  const switchOrganization = useCallback((orgId: string, orgType: 'school' | 'recruiter') => {
    const org = state.organizations.find((o) => o.id === orgId && o.type === orgType)
    if (org) {
      localStorage.setItem('selectedOrgId', orgId)
      localStorage.setItem('selectedOrgType', orgType)
      setState((prev) => ({ ...prev, organization: org }))
    }
  }, [state.organizations])

  const signUp = async (
    email: string,
    password: string,
    userType: UserType,
    fullName: string
  ): Promise<{ error: string | null }> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            user_type: userType,
            full_name: fullName,
          },
        },
      })

      if (error) {
        return { error: formatAuthError(error) }
      }

      // If email confirmation is required
      if (data.user && !data.session) {
        return { error: null }
      }

      return { error: null }
    } catch (err) {
      console.error('Sign up error:', err)
      return { error: 'An unexpected error occurred. Please try again.' }
    }
  }

  const signIn = async (
    email: string,
    password: string
  ): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        return { error: formatAuthError(error) }
      }

      // The onAuthStateChange listener handles loading state and profile fetch
      return { error: null }
    } catch (err) {
      console.error('Sign in error:', err)
      return { error: 'An unexpected error occurred. Please try again.' }
    }
  }

  const signOut = async (): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.signOut()

      if (error) {
        return { error: formatAuthError(error) }
      }

      // The onAuthStateChange listener handles state cleanup
      return { error: null }
    } catch (err) {
      console.error('Sign out error:', err)
      return { error: 'An unexpected error occurred. Please try again.' }
    }
  }

  const resetPassword = async (email: string): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      })

      if (error) {
        return { error: formatAuthError(error) }
      }

      return { error: null }
    } catch (err) {
      console.error('Reset password error:', err)
      return { error: 'An unexpected error occurred. Please try again.' }
    }
  }

  const updatePassword = async (
    newPassword: string
  ): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) {
        return { error: formatAuthError(error) }
      }

      return { error: null }
    } catch (err) {
      console.error('Update password error:', err)
      return { error: 'An unexpected error occurred. Please try again.' }
    }
  }

  // Convenience getters for organization IDs
  const schoolId = state.organization?.type === 'school' ? state.organization.id : undefined
  const recruiterId = state.organization?.type === 'recruiter' ? state.organization.id : undefined

  const value: AuthContextType = {
    ...state,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
    refreshOrganization,
    switchOrganization,
    schoolId,
    recruiterId,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
